/**
 * Sign-in policy and account state errors from the API (design 17.1, OP-07). Any request can meet one; the
 * auth provider listens and shows the right screen instead of each page showing an error.
 */
export type PolicyBlock = 'mfa_required' | 'mfa_enrol_required' | 'sso_required' | 'company_suspended'
export const POLICY_CODES: readonly PolicyBlock[] = ['mfa_required', 'mfa_enrol_required', 'sso_required', 'company_suspended']

type Listener = (code: PolicyBlock, message: string) => void
const listeners = new Set<Listener>()
export function onPolicyBlock(fn: Listener) { listeners.add(fn); return () => { listeners.delete(fn) } }
export function emitPolicyBlock(code: PolicyBlock, message: string) { for (const l of listeners) l(code, message) }

export function isPolicyBlock(code: unknown): code is PolicyBlock {
  return typeof code === 'string' && (POLICY_CODES as readonly string[]).includes(code)
}

/** Reads a 403 response (without consuming it) and reports a policy block. Returns the code, if any. */
export async function inspectForbidden(res: Response): Promise<PolicyBlock | null> {
  if (res.status !== 403) return null
  try {
    const j = await res.clone().json() as { error?: { code?: string; message?: string } }
    const code = j?.error?.code
    if (isPolicyBlock(code)) { emitPolicyBlock(code, j.error?.message ?? ''); return code }
  } catch { /* not JSON */ }
  return null
}
